import {
  startTransition,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
  type Ref,
  type TextareaHTMLAttributes,
} from 'react';
import { flushSync } from 'react-dom';
import { flushesFirst, syncBox } from '../lib/noteTextSync.js';

type Props = Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'value' | 'defaultValue' | 'onChange'
> & {
  /** The note's copy of the text. */
  value: string;
  /** The text as typed or pasted (sent in the background while typing; at once before any action). */
  onText: (v: string) => void;
  /** Side work on each keystroke that needs the box itself (the dot phrase check); runs at once. */
  onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  ref?: Ref<HTMLTextAreaElement>;
};

/**
 * ehr#910: a note's text box that never drops a keystroke (lib/noteTextSync says why). The box
 * holds the text as typed; the note gets it in the background (a React transition), so a slow
 * re-draw of the note never holds up the next letter. The note always has the full text before it
 * acts on it: when the box loses focus, on a press anywhere (a button that keeps the focus in the
 * box too), and on Enter, Tab, Escape or a shortcut (Ctrl+S, Ctrl+Enter).
 */
export function NoteTextarea({ value, onText, onChange, onBlur, onKeyDown, ref, ...rest }: Props) {
  const box = useRef<HTMLTextAreaElement | null>(null);
  // texts sent to the note that haven't come back yet (the note can be a few keystrokes behind)
  const sent = useRef<string[]>([]);
  const send = useRef(onText);
  send.current = onText;
  const [initial] = useState(value);
  const disarm = useRef<(() => void) | null>(null);

  /** The note gets the box's text now, if it hasn't caught up. */
  const flush = () => {
    disarm.current?.();
    const el = box.current;
    if (el && sent.current.length) send.current(el.value);
  };
  const flushRef = useRef(flush);
  flushRef.current = flush;
  // the latest key handler: after the note catches up (below) it reads the full text
  const keyRef = useRef(onKeyDown);
  keyRef.current = onKeyDown;

  // A press anywhere while the note is behind: it catches up first, so the button pressed (Save,
  // Sign, Generate, a phrase pill that keeps the focus in the box) works on the full text.
  const arm = () => {
    if (disarm.current || typeof document === 'undefined') return;
    const down = () => flushSync(() => flushRef.current());
    document.addEventListener('pointerdown', down, true);
    disarm.current = () => {
      document.removeEventListener('pointerdown', down, true);
      disarm.current = null;
    };
  };

  // What the note says now: its own text catching up, or a change from outside the box.
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const r = syncBox(el.value, sent.current, value);
    sent.current = r.sent;
    if (el.value !== r.box) el.value = r.box;
    if (!r.sent.length) disarm.current?.();
  }, [value]);

  // Leaving with text the note doesn't have yet (the box closing): it gets it.
  useEffect(() => {
    const el = box.current;
    return () => {
      disarm.current?.();
      if (el && sent.current.length) send.current(el.value);
    };
  }, []);

  return (
    <textarea
      {...rest}
      ref={(el) => {
        box.current = el;
        if (typeof ref === 'function') ref(el);
        else if (ref) ref.current = el;
      }}
      defaultValue={initial}
      onChange={(e) => {
        const v = e.target.value;
        sent.current.push(v);
        onChange?.(e);
        arm();
        startTransition(() => send.current(v));
      }}
      onKeyDown={(e) => {
        if (flushesFirst(e)) flushSync(() => flushRef.current());
        keyRef.current?.(e);
      }}
      onBlur={(e) => {
        flushRef.current();
        onBlur?.(e);
      }}
    />
  );
}
